// apps/admin/src/lib/fxRate.ts
//
// Phase 8.1 Wave 2 Task 06 — Live FX rate hook.
//
// vg-binding: CONTEXT:decisions=P8.D-150,P8.D-151,P8.D-152,P8.D-157,P8.D-186,P8.D-196,P8.D-251,P8.D-466
// vg-binding: BR:business-rules=BR-8
// vg-binding: CONTEXT:UI-SPEC=§0.1,§12.2
// vg-binding: API-CONTRACTS:fx-rates-endpoint=P6.D-230
//
// `useFxRate(displayCcy)` — TanStack Query wrapper around the live FX
// endpoint `GET /api/v1/fx-rates?from=USD&to={ccy}` shipped by Phase 6
// . USD is the canonical currency;
// every Money cell in the admin portal renders the USD source amount and
// uses this hook to resolve a live display-currency estimate for the
// `(≈ {CCY} Y.YY)` tooltip companion.
//
// Caching policy:
//   • staleTime = 5 min   — FX rates change slowly; avoids burst refetches
//                            while a long-running admin shift hops surfaces.
//   • gcTime    = 30 min  — keep the cached rate for re-entry to the same
//                            surface without a re-fetch.
//   • refetchOnWindowFocus = true — pull a fresh rate when the admin tabs
//                            back in after a break (matches staleness
//                            posture for FX-sensitive data).
//   • retry = 1            — single retry on transient network glitch.
//
// Returned envelope: `{ rate, fetchedAt, isStale, isLoading, isError }`.
// `rate` is null until the first successful fetch; `Money.tsx` falls back
// to USD-only display in that case. `isStale` flips when the data is older
// than `STALE_THRESHOLD_MS` and is surfaced to the tooltip so admins know
// they're looking at a stale conversion.
//
// USD ⇒ USD short-circuit: when `displayCcy === 'USD'` (or no displayCcy)
// the hook returns `{ rate: 1, fetchedAt: now, isStale: false, ... }`
// synchronously without hitting the network — Money components on
// USD-only surfaces don't pay the round-trip cost.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

/** Stale threshold — 5 minutes matches React Query staleTime. */
export const STALE_THRESHOLD_MS = 5 * 60 * 1000;

/** TanStack Query cache GC time — 30 minutes. */
const GC_TIME_MS = 30 * 60 * 1000;

/** API base URL — falls back to `/api/v1` per Wave 2 shared convention. */
function apiBaseUrl(): string {
  const fromEnv =
    typeof import.meta !== 'undefined' && import.meta.env
      ? (import.meta.env.VITE_API_BASE_URL as string | undefined)
      : undefined;
  return fromEnv ?? '/api/v1';
}

/** Wire shape — server response for GET /api/v1/fx-rates. */
interface FxRateResponseEnvelope {
  ok?: boolean;
  data?: {
    from: string;
    to: string;
    rate: number;
    fetchedAt: string | number;
  };
  // Legacy shape some FX providers use directly (compatibility-only).
  rate?: number;
  fetchedAt?: string | number;
  error?: { code?: string; message?: string; user_message?: string };
}

/** Result shape consumed by `<Money>` and any other live-FX surface. */
export interface UseFxRateResult {
  /** Live rate USD→displayCcy, or null when not yet loaded/error. */
  rate: number | null;
  /** Epoch ms when the cached rate was fetched. null when never loaded. */
  fetchedAt: number | null;
  /** True when the cached rate is older than STALE_THRESHOLD_MS. */
  isStale: boolean;
  /** TanStack Query loading state — true during the first fetch. */
  isLoading: boolean;
  /** TanStack Query fetching state — true on background refetch too. */
  isFetching: boolean;
  /** Last error message (sanitized) — null when none. */
  errorMessage: string | null;
}

/**
 * Live FX rate hook. Returns USD→{displayCcy} conversion factor with
 * 5-minute staleness window and refetch-on-focus.
 *
 * @example
 *   const { rate, isStale } = useFxRate('EUR');
 *   if (rate != null) console.log(`1 USD = ${rate} EUR`);
 */
export function useFxRate(displayCcy: string | undefined): UseFxRateResult {
  const target = (displayCcy ?? 'USD').toUpperCase();
  const isUsdSource = target === 'USD';

  const query: UseQueryResult<{
    rate: number;
    fetchedAt: number;
  }> = useQuery({
    queryKey: ['fxRate', 'USD', target],
    enabled: !isUsdSource,
    staleTime: STALE_THRESHOLD_MS,
    gcTime: GC_TIME_MS,
    refetchOnWindowFocus: true,
    retry: 1,
    queryFn: async () => {
      const url = `${apiBaseUrl()}/fx-rates?from=USD&to=${encodeURIComponent(
        target,
      )}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        let msg = `HTTP ${res.status}`;
        try {
          const parsed = JSON.parse(text) as FxRateResponseEnvelope;
          msg =
            parsed?.error?.user_message ??
            parsed?.error?.message ??
            msg;
        } catch {
          /* keep HTTP fallback */
        }
        throw new Error(msg);
      }
      const body = (await res.json()) as FxRateResponseEnvelope;
      // Accept both envelope-wrapped + flat legacy shapes per
      // INTERFACE-STANDARDS.md (legacy compact shape acceptance clause).
      const rate = body?.data?.rate ?? body?.rate;
      const fetchedAtRaw = body?.data?.fetchedAt ?? body?.fetchedAt;
      if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) {
        throw new Error('FX rate response missing or invalid');
      }
      const fetchedAt =
        typeof fetchedAtRaw === 'number'
          ? fetchedAtRaw
          : fetchedAtRaw
            ? Date.parse(String(fetchedAtRaw))
            : Date.now();
      return { rate, fetchedAt };
    },
  });

  if (isUsdSource) {
    return {
      rate: 1,
      fetchedAt: Date.now(),
      isStale: false,
      isLoading: false,
      isFetching: false,
      errorMessage: null,
    };
  }

  const data = query.data;
  const fetchedAt = data?.fetchedAt ?? null;
  const isStale =
    fetchedAt != null && Date.now() - fetchedAt > STALE_THRESHOLD_MS;

  const errMsg =
    query.error instanceof Error
      ? query.error.message
      : query.error
        ? String(query.error)
        : null;

  return {
    rate: data?.rate ?? null,
    fetchedAt,
    isStale,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    errorMessage: errMsg,
  };
}
